// metricAnswer.js - question in, grounded answer out.
//
// G360_K505 - three additions. Every metric registered before keeps its
// counts, its fields and its rules; what changes is below.
//   - a registry entry may carry its own `counts` (the move this header
//     always asked for); they are merged over the table below.
//   - a count may name counts to state `alongside` it, so "0 expired" is
//     never said without how many were not measured.
//   - "which agents have X" with a count key lists THOSE agents rather
//     than the first 25 rows of every agent (true of the older metrics'
//     counts too - "which agents have no owner" was answered from the
//     first 25 agents, owned or not). An entry marked `paged` is read a
//     page at a time so a count covers every row.
//
//   route()    the model sees ONLY the catalogue - titles, descriptions,
//              example questions, and the named counts each metric can
//              produce. Never the schema, never a table name, never SQL.
//   run()      the chosen rpc executes through the console's own supabase
//              client, as the signed-in user, under row-level security.
//   narrate()  the model turns a small, projected payload into a sentence
//              and is forbidden to introduce a number that is not in it.
//
// WHAT CHANGED AFTER THE FIRST VERSION 502'd
//
// The first cut sent every returned row to the narrating call. For
// ai_exposure_score that is one row and it worked. For agent_risk_score
// it is one row PER AGENT - 646 at Collette - each carrying a drivers
// jsonb and an unmeasured_drivers array. Megabytes of JSON into a
// Netlify function with a ten-second budget: 502 every time, surfaced as
// "The answer could not be written up."
//
// The registry already marked those fields as "evidence for the
// drill-down, not for prose". The rule was declared and not implemented.
// Now it is: only value, flag and unmeasured fields are sent, detail
// fields never leave the browser, and a list is capped.
//
// AND A QUESTION HAS A SHAPE, NOT JUST A METRIC
//
// "How many agents have no owner" and "which agents have no owner" want
// the same metric summarised two different ways. Sending 646 rows and
// hoping the model tallies them is both wasteful and unreliable -
// counting is arithmetic and belongs in code.
//
// So a count is computed HERE, from a named predicate WE declare, and the
// model receives the number rather than the rows. The answer is exact by
// construction rather than by the model counting well. The model still
// chooses which count applies; it never invents one.

import { supabase } from '@govern360/shared';
import { askAI } from './aiApi.js';
import { METRIC_BY_KEY, METRICS } from './metricRegistry.js';

// How many rows a list answer may carry. Enough to be useful, small
// enough to survive the function's time and size budget.
const LIST_CAP = 25;

/* Named counts, per metric. Declared rather than inferred, for the same
   reason the metrics are: a model choosing from a fixed list of questions
   we can answer is safe; a model composing a predicate is a query
   language with extra steps.

   These belong in metricRegistry.js beside the metric they describe, and
   should move there once the shape has proven itself. */
const COUNTS = {
  agent_risk: {
    no_owner:  { label: 'agents missing at least one required owner',
                 test: (r) => Number(r.owners_missing || 0) > 0 },
    unranked:  { label: 'agents below 50 per cent measured evidence, which are not ranked',
                 test: (r) => Number(r.measured_pct || 0) < 50 },
    high_band: { label: 'agents in a high or critical risk band',
                 test: (r) => /high|critical/i.test(String(r.risk_band || '')) },
  },
  tool_risk: {
    high_band: { label: 'tools in a high or critical risk band',
                 test: (r) => /high|critical/i.test(String(r.risk_band || '')) },
    unranked:  { label: 'tools below 50 per cent measured evidence',
                 test: (r) => Number(r.measured_pct || 0) < 50 },
  },
  /* g360-k376 - the trifecta count is the one worth having. It is a
     property of the composition, so no per-agent test suite produces
     it: neither end of a path fails a review of itself. */
  exposure_legs: {
    trifecta:    { label: 'agents holding the full trifecta - untrusted input in, private data read, shared substrate written, external send',
                   test: (r) => r.full_trifecta === true },
    untrusted:   { label: 'agents that take untrusted input',
                   test: (r) => r.untrusted_in === true },
    external:    { label: 'agents that can send externally',
                   test: (r) => r.external_out === true },
    in_and_out:  { label: 'agents that both take untrusted input and can send externally',
                   test: (r) => r.untrusted_in === true && r.external_out === true },
  },
  agent_signal: {
    unowned:     { label: 'agents with no owner recorded',
                   test: (r) => !r.owner_name },
    shared:      { label: 'agents in a shared tenant',
                   test: (r) => r.shared_tenant === true },
  },
  exposure_risks: {
    measured:   { label: 'risks with a measured metric behind them',
                  test: (r) => r.measured === true },
    unmeasured: { label: 'risks raised without a measured metric',
                  test: (r) => r.measured !== true },
  },
};

/* G360_K505 - the counts one metric can produce: its registry entry's own,
   over any declared here. */
function countsFor(key) {
  const entry = METRIC_BY_KEY[key];
  return Object.assign({}, COUNTS[key] || {}, (entry && entry.counts) || {});
}

// G360_K505 - a paged read stops here. 20 pages of 1000 rows is every agent
// in any estate seen so far; past it, a count says it is partial.
const PAGE = 1000;
const MAX_PAGES = 20;

function catalogueForRouter() {
  return METRICS.map((m) => {
    const c = countsFor(m.key);
    const counts = Object.keys(c).map((k) => ({ count: k, of: c[k].label }));
    return {
      key: m.key,
      title: m.title,
      description: m.description,
      examples: m.examples,
      params: m.params || null,
      counts: counts.length ? counts : null,
    };
  });
}

const ROUTER_CONTRACT = [
  'Choose which single pre-built metric answers the user question.',
  'You have no database access and must not write SQL.',
  'Reply with JSON only, no prose, no code fences.',
  '',
  'If the question asks HOW MANY and the metric lists a matching count:',
  '  {"key":"<metric>","count":"<count key>"}',
  'If the question asks WHICH, or wants the items themselves:',
  '  {"key":"<metric>","shape":"list"}',
  '  and if a listed count names exactly the items asked for, add it to',
  '  keep only those items: {"key":"<metric>","shape":"list","count":"<count key>"}',
  'Otherwise:',
  '  {"key":"<metric>"}',
  'If no metric answers it:',
  '  {"refuse":"<one short sentence on what is missing>"}',
  '',
  'Only use a count key listed against that metric. Do not invent one.',
  'If the metric lists a window_days param and the question names a period',
  '(this month, next 90 days), add "params":{"window_days":<days>}.',
  'If the metric lists another param and the question supplies it, add it to',
  'params in the listed format (a month as "YYYY-MM"; a value from one_of).',
  'Refusing is correct and expected - a near-miss is worse than a refusal,',
  'because the user cannot tell the difference.',
].join('\n');

function parseJson(text) {
  if (!text) return null;
  const cleaned = String(text).replace(/```json/gi, '').replace(/```/g, '').trim();
  const start = cleaned.indexOf('{');
  const end = cleaned.lastIndexOf('}');
  if (start === -1 || end === -1 || end < start) return null;
  try { return JSON.parse(cleaned.slice(start, end + 1)); } catch (e) { return null; }
}

/* Step 1. Pick a metric and a shape, or refuse. */
export async function route(question) {
  const context = ROUTER_CONTRACT + '\n\nAVAILABLE METRICS:\n'
    + JSON.stringify(catalogueForRouter(), null, 1);

  const { answer, error } = await askAI({ question, context, mode: 'assistant' });
  if (error) return { refuse: 'The assistant is unavailable right now.', transport: true };

  const parsed = parseJson(answer);
  if (!parsed) return { refuse: 'I could not work out which measure that question is asking for.' };
  if (parsed.refuse) return { refuse: String(parsed.refuse) };
  if (!parsed.key || !METRIC_BY_KEY[parsed.key]) {
    return { refuse: 'That is not something Govern360 measures today.' };
  }
  // A count the model invented is not a count we can compute. Drop it and
  // answer as a list rather than guessing at what was meant.
  const known = countsFor(parsed.key);
  const count = (parsed.count && known[parsed.count]) ? parsed.count : null;
  return { key: parsed.key, params: parsed.params || {}, count, shape: parsed.shape || null };
}

/* Step 2. Run it as the user. RLS does the tenant filtering. */
export async function run(key, params, tenantId) {
  const entry = METRIC_BY_KEY[key];
  if (!entry) return { error: 'Unknown metric.' };
  if (!entry.tenantImplicit && !tenantId) return { error: 'No active organisation.' };

  if (entry.paged) {
    // G360_K505 - the same page loop the page that renders it uses.
    const args = entry.args(tenantId, params);
    const all = [];
    let partial = false;
    for (let i = 0; i < MAX_PAGES; i++) {
      const { data, error } = await supabase.rpc(entry.rpc, args).range(i * PAGE, i * PAGE + PAGE - 1);
      if (error) return { error: error.message };
      const page = Array.isArray(data) ? data : (data ? [data] : []);
      page.forEach((r) => all.push(r));
      if (page.length < PAGE) break;
      if (i === MAX_PAGES - 1) partial = true;
    }
    return { entry, rows: typeof entry.pick === 'function' ? entry.pick(all) : all, partial };
  }

  const { data, error } = await supabase.rpc(entry.rpc, entry.args(tenantId, params));
  if (error) return { error: error.message };

  const rows = Array.isArray(data) ? data : (data ? [data] : []);
  // G360_K507 - an entry may answer from part of what its function
  // returns (the exposure findings are an array inside the score row).
  return { entry, rows: typeof entry.pick === 'function' ? entry.pick(rows) : rows };
}

/* Keep only what may be stated. Everything the registry marks as detail -
   drivers, dimensions, findings, contributors - stays in the browser for
   the drill-down and never enters a prompt. That is what broke the first
   version, and it is the difference between a 2 KB payload and a 2 MB one. */
function project(entry, rows) {
  const keep = []
    .concat(entry.value || [])
    .concat(entry.flag || [])
    .concat(entry.unmeasured || []);
  if (keep.length === 0) return rows;
  return rows.map((r) => {
    const out = {};
    keep.forEach((f) => { if (r[f] !== undefined) out[f] = r[f]; });
    return out;
  });
}

function narrationRules(entry) {
  const lines = [
    'Answer the question using ONLY the data below.',
    'Every number you state must appear in it. Do not add, average, total,',
    'project or infer any figure that is not there.',
    'Do not explain causes the data does not contain. Do not describe a',
    'trend unless it contains two periods.',
    'Be brief. Two or three sentences unless the question needs a list.',
  ];
  if (entry.flag && entry.flag.length) {
    lines.push(
      'MEASUREMENT FLAGS: ' + entry.flag.join(', ') + '.',
      'Where a flag is false, or a *_is_sample flag is true, the value beside',
      'it is not a fact about this organisation. Say so plainly or leave it',
      'out. Never present seeded or unmeasured values as the estate.');
  }
  if (entry.unmeasured && entry.unmeasured.length) {
    lines.push('NOT MEASURED: ' + entry.unmeasured.join(', ') + '.',
      'Name what was not measured. Never render it as zero, none, or good.');
  }
  if (entry.alwaysWith && entry.alwaysWith.length) {
    lines.push('ALWAYS INCLUDE: ' + entry.alwaysWith.join(', ')
      + '. The figure is not honest without it.');
  }
  if (entry.caveat) lines.push('CAVEAT: ' + entry.caveat);
  return lines;
}

/* G360_K505 - rules for figures that must be stated with the answer, and
   for a read that stopped before the end. */
function alsoLines(also, partial, total) {
  const lines = [];
  if (also && also.length) {
    lines.push('', 'ALSO STATE, in the same answer, each of these counts. Without them the',
      'figure above reads cleaner than the estate is:',
      JSON.stringify(also, null, 1));
  }
  if (partial) {
    lines.push('', 'The read stopped at ' + total + ' rows before reaching the end. Say every',
      'figure is of the first ' + total + ' rows only, never of the whole estate.');
  }
  return lines;
}

export async function narrate(question, entry, rows, count, listOf, partial) {
  /* G360_K506 - an entry may say what "nothing" means for it (emptyText),
     and when a row of nulls is still nothing (isEmpty). */
  const empty = !rows || rows.length === 0 || (typeof entry.isEmpty === 'function' && entry.isEmpty(rows));
  if (empty) {
    return { text: entry.emptyText || 'Nothing was recorded for that. It is measuring nothing rather than measuring zero.', empty: true };
  }

  const lines = narrationRules(entry);

  if (count) {
    /* The arithmetic is already done. The model writes a sentence around a
       number rather than producing one. */
    lines.push('', 'METRIC: ' + entry.title,
      'This is a counted answer. State the count as given, do not recount,',
      'and do not list the items - they were not sent.',
      '', JSON.stringify({ counted: count.label, count: count.value, out_of: count.total }, null, 1));
    alsoLines(count.also, count.partial, count.total).forEach((l) => lines.push(l));
  } else {
    /* G360_K505 - with listOf, rows are already only the items asked
       about; listOf says what they are and out of how many. */
    const projected = project(entry, rows);
    const shown = projected.slice(0, LIST_CAP);
    lines.push('', 'METRIC: ' + entry.title, entry.description);
    if (listOf) {
      lines.push('', 'These rows are ONLY the ' + listOf.label + ': ' + listOf.matched
        + ' of ' + listOf.total + ' in all. That count is exact; state it.');
      alsoLines(listOf.also, listOf.partial, listOf.total).forEach((l) => lines.push(l));
    } else if (partial) {
      alsoLines(null, true, rows.length).forEach((l) => lines.push(l));
    }
    if (projected.length > shown.length) {
      // The model must not imply it saw everything, so tell it plainly.
      lines.push('', 'This is the first ' + shown.length + ' of ' + projected.length
        + ' rows. Say so if you summarise them, and do not state a total you were not given.');
    }
    lines.push('', 'ROWS:', JSON.stringify(shown, null, 1));
  }

  const { answer, error } = await askAI({ question, context: lines.join('\n'), mode: 'assistant' });
  if (error) return { error };
  return { text: answer };
}

/* The whole path. */
export async function ask(question, tenantId, onGap) {
  const routed = await route(question);
  if (routed.refuse) {
    if (!routed.transport && typeof onGap === 'function') {
      try { onGap({ question, reason: routed.refuse, stage: 'route' }); } catch (e) { /* never break the answer */ }
    }
    return { refused: true, text: routed.refuse };
  }

  const ran = await run(routed.key, routed.params, tenantId);
  if (ran.error) return { refused: true, text: 'That measure could not be read: ' + ran.error, failed: true };

  let count = null;
  let listOf = null;
  let narrated = ran.rows;
  if (routed.count) {
    const defs = countsFor(routed.key);
    const def = defs[routed.count];
    if (def) {
      /* G360_K505 - the counts to state beside this one, computed the same
         way. A name the entry does not declare is skipped, never guessed. */
      const also = (def.alongside || []).filter((k) => defs[k]).map((k) => ({
        counted: defs[k].label, count: ran.rows.filter(defs[k].test).length,
      }));
      const matched = ran.rows.filter(def.test);
      if (routed.shape === 'list' && matched.length > 0) {
        // "Which agents have X": those agents, not the first page of all.
        listOf = { label: def.label, matched: matched.length, total: ran.rows.length, also, partial: !!ran.partial };
        narrated = matched;
      } else {
        // A count - or a list of nothing, which is a count of zero.
        count = { label: def.label, value: matched.length, total: ran.rows.length, also, partial: !!ran.partial };
      }
    }
  }

  const said = await narrate(question, ran.entry, narrated, count, listOf, !!ran.partial);
  if (said.error) return { refused: true, text: 'The answer could not be written up.', failed: true };

  return {
    text: said.text,
    metric: { key: routed.key, title: ran.entry.title, rpc: ran.entry.rpc },
    // The drill-down gets the FULL rows, including the detail fields the
    // narrator never saw. Provenance should be richer than the prose, not
    // the same thing twice.
    rows: ran.rows,
    count,
    empty: !!said.empty,
  };
}
